import { useState, useRef, useCallback, useEffect } from "react";
import _ from "lodash";
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  GridEventListener,
  GridRowId,
  GridRowEditStopReasons,
  GridCellParams,
} from "@mui/x-data-grid-premium";

interface UseRowEditingOptions {
  apiRef: React.RefObject<any>;
}

interface UseRowEditingReturn {
  rows: GridRowsProp;
  setRows: React.Dispatch<React.SetStateAction<GridRowsProp>>;
  rowModesModel: GridRowModesModel;
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>;
  originalRowsRef: React.MutableRefObject<Record<GridRowId, any>>;
  handleEditClick: (id: GridRowId) => () => void;
  handleSaveClick: (id: GridRowId) => () => void;
  handleCancelClick: (id: GridRowId) => () => void;
  handleRowModesModelChange: (newRowModesModel: GridRowModesModel) => void;
  handleRowEditStop: GridEventListener<"rowEditStop">;
  handleCellKeyDown: (
    params: GridCellParams,
    event: React.KeyboardEvent & { defaultMuiPrevented?: boolean }
  ) => void;
}

/**
 * Hook for managing row editing state, snapshots, and mode handlers.
 */
export function useRowEditing({
  apiRef,
}: UseRowEditingOptions): UseRowEditingReturn {
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  // Store original row snapshots captured before MUI mutations
  const originalRowsRef = useRef<Record<GridRowId, any>>({});

  // Subscribe to rowEditStart event to capture original data before MUI mutations
  useEffect(() => {
    if (!apiRef.current) return;

    const handleRowEditStart: GridEventListener<"rowEditStart"> = (params) => {
      // Get the unmutated row from grid's internal store
      const originalRow = apiRef.current?.getRow(params.id);
      if (originalRow) {
        // Deep clone to preserve state before MUI mutations
        originalRowsRef.current[params.id] = _.cloneDeep(originalRow);
      }
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      "rowEditStart",
      handleRowEditStart
    );

    return () => unsubscribe();
  }, [apiRef]);

  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }));
    },
    []
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }));
    },
    []
  );

  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));

      const editedRow = rows.find((row) => row.id === id);
      if (editedRow?.isNew) {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      }
    },
    [rows]
  );

  const handleRowModesModelChange = useCallback(
    (newRowModesModel: GridRowModesModel) => {
      setRowModesModel(newRowModesModel);
    },
    []
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = useCallback(
    (params, event) => {
      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
        event.defaultMuiPrevented = true;
      }
    },
    []
  );

  const handleCellKeyDown = useCallback(
    (
      params: GridCellParams,
      event: React.KeyboardEvent & { defaultMuiPrevented?: boolean }
    ) => {
      if (event.key !== "Escape") return;
      if (rowModesModel[params.id]?.mode !== GridRowModes.Edit) return;

      event.defaultMuiPrevented = true;
      setRowModesModel((prev) => ({
        ...prev,
        [params.id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    },
    [rowModesModel]
  );

  return {
    rows,
    setRows,
    rowModesModel,
    setRowModesModel,
    originalRowsRef,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    handleRowEditStop,
    handleCellKeyDown,
  };
}
